import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCamera, FaTools, FaAd, FaShieldAlt, FaWifi, FaMoneyBillWave, FaStore, FaExternalLinkAlt, FaUsers, FaBullhorn, FaMapMarkerAlt, FaPlug, FaQuestionCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import Navbar from '../Navbar';

const Section = styled.section`
  padding: 160px 24px;
  padding-top: 200px;
  background: linear-gradient(180deg, #000000 0%, #0A0A0A 100%);
  color: #fff;
  overflow: hidden;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Overline = styled.span`
  color: #FF5A5A;
  font-weight: 600;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 8px;
  display: block;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 24px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Divider = styled.div`
  width: 60px;
  height: 4px;
  background: #FF5A5A;
  margin: 40px auto;
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 40px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: #fff;
`;

const CardContent = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 1.1rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin: 80px 0;
`;

const BenefitCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 40px;
  text-align: left;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const BenefitIcon = styled.div`
  font-size: 2.5rem;
  color: #FF5A5A;
  margin-bottom: 24px;
`;

const BenefitTitle = styled.h3`
  font-size: 1.75rem;
  margin-bottom: 16px;
  color: #fff;
  font-weight: 700;
`;

const BenefitDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  font-size: 1.1rem;
`;

const PartnersSection = styled.div`
  margin: 80px 0;
  text-align: center;
`;

const PartnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 32px;
  margin-top: 40px;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
`;

const PartnerCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.07);
  border: 1.5px solid rgba(255, 90, 90, 0.15);
  border-radius: 18px;
  padding: 32px 20px 24px 20px;
  text-align: center;
  box-shadow: 0 4px 24px 0 rgba(0,0,0,0.10);
  transition: transform 0.18s cubic-bezier(.4,0,.2,1), box-shadow 0.18s cubic-bezier(.4,0,.2,1), background 0.18s;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-6px) scale(1.03);
    background: rgba(255, 90, 90, 0.10);
    box-shadow: 0 8px 32px 0 rgba(255,90,90,0.10), 0 2px 8px 0 rgba(0,0,0,0.10);
  }
`;

const PartnerIcon = styled.div`
  font-size: 2.2rem;
  color: #FF5A5A;
  margin-bottom: 12px;
`;

const PartnerName = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
  word-break: break-word;
`;

const PartnerLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #FF5A5A;
  background: rgba(255,255,255,0.08);
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  margin-top: 6px;
  transition: background 0.18s, color 0.18s;

  &:hover {
    background: #FF5A5A;
    color: #fff;
  }
`;

const ContactSection = styled.div`
  text-align: center;
  margin: 80px 0;
  padding: 40px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
`;

const ContactName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 8px;
  color: #fff;
`;

const ContactTitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 16px;
`;

const ContactPhone = styled.a`
  color: #FF5A5A;
  text-decoration: none;
  font-size: 1.25rem;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 120px;
position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -40px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: #FF5A5A;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(3rem, 8vw, 5rem);
  font-weight: 900;
  color: #fff;
  margin-bottom: 24px;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: -1px;
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin: 60px 0;
  text-align: center;
`;

const StatCard = styled.div`
  padding: 30px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #FF5A5A;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  background: #FF5A5A;
  color: white;
  padding: 16px 32px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  margin-top: 40px;
  transition: all 0.3s ease;

  &:hover {
    background: #ff4040;
    transform: translateY(-2px);
  }
`;

const ValuePropsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 36px;
  margin: 60px 0 40px 0;
`;

const ValueCard = styled(motion.div)`
  background: rgba(255,255,255,0.06);
  border: 1.5px solid rgba(255,90,90,0.13);
  border-radius: 16px;
  padding: 36px 28px 28px 28px;
  box-shadow: 0 2px 16px 0 rgba(0,0,0,0.10);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.18s, box-shadow 0.18s, background 0.18s;
  min-height: 260px;

  &:hover {
    transform: translateY(-4px) scale(1.02);
    background: rgba(255,90,90,0.10);
    box-shadow: 0 8px 32px 0 rgba(255,90,90,0.10), 0 2px 8px 0 rgba(0,0,0,0.10);
  }
`;

const ValueIcon = styled.div`
  font-size: 2.2rem;
  color: #FF5A5A;
  margin-bottom: 18px;
`;

const ValueTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 12px;
`;

const ValueText = styled.p`
  color: rgba(255,255,255,0.85);
  font-size: 1.08rem;
  margin-bottom: 0;
  line-height: 1.6;
`;

const BulletList = styled.ul`
  margin: 12px 0 0 0;
  padding-left: 20px;
  color: rgba(255,255,255,0.8);
  font-size: 1.05rem;
  list-style: disc inside;
`;

const Em = styled.em`
  color: #FF5A5A;
  font-style: normal;
  font-weight: 600;
`;

const InfoRow = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 32px;
  background: rgba(255,255,255,0.04);
  border-radius: 18px;
  border: 1.5px solid rgba(255,90,90,0.10);
  padding: 36px 32px 28px 32px;
  margin: 0 auto 36px auto;
  max-width: 900px;
  box-shadow: 0 2px 16px 0 rgba(0,0,0,0.08);
  flex-wrap: wrap;

  @media (max-width: 800px) {
    flex-direction: column;
    padding: 28px 16px;
    gap: 18px;
  }
`;

const InfoIcon = styled.div`
  font-size: 2.5rem;
  color: #FF5A5A;
  flex-shrink: 0;
  margin-right: 12px;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
`;

const InfoList = styled.ul`
  margin: 0 0 10px 0;
  padding-left: 20px;
  color: rgba(255,255,255,0.85);
  font-size: 1.08rem;
  list-style: disc inside;
`;

const InfoBlockquote = styled.div`
  border-left: 4px solid #FF5A5A;
  background: rgba(255,90,90,0.08);
  color: #fff;
  padding: 16px 24px;
  margin-top: 18px;
  margin-bottom: 0;
  font-size: 1.08rem;
  font-style: italic;
  line-height: 1.7;
  border-radius: 8px;
`;

const InfoCallout = styled.div`
  color: #fff;
  background: rgba(255,90,90,0.13);
  border-radius: 8px;
  padding: 12px 18px;
  font-weight: 600;
  margin-top: 18px;
  font-size: 1.08rem;
  letter-spacing: 0.01em;
  border-left: 4px solid #FF5A5A;
`;

const CatchRow = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 32px;
  background: linear-gradient(90deg, #1a1a1a 80%, #FF5A5A 100%);
  border-radius: 18px;
  border: 1.5px solid rgba(255,90,90,0.13);
  padding: 40px 32px 32px 32px;
  box-shadow: 0 2px 16px 0 rgba(0,0,0,0.08);
  max-width: 900px;
  margin: 0 auto 60px auto;
  @media (max-width: 800px) {
    flex-direction: column;
    padding: 28px 16px;
    gap: 18px;
  }
`;

const CatchIcon = styled.div`
  font-size: 2.8rem;
  color: #FF5A5A;
  flex-shrink: 0;
  margin-right: 8px;
  margin-top: 4px;
`;

const CatchContent = styled.div`
  flex: 1;
`;

const CatchHeadline = styled.h2`
  color: #FF5A5A;
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 12px;
`;

const CatchText = styled.p`
  color: rgba(255,255,255,0.92);
  font-size: 1.13rem;
  line-height: 1.7;
  margin-bottom: 0;
`;

const CatchCallout = styled.div`
  color: #fff;
  background: rgba(255,90,90,0.13);
  border-radius: 8px;
  padding: 12px 18px;
  font-weight: 600;
  margin-top: 18px;
  font-size: 1.08rem;
  letter-spacing: 0.01em;
`;

const CatchSimpleSection = styled(motion.section)`
  max-width: 700px;
  margin: 0 auto 60px auto;
  padding: 0;
  position: relative;
`;

const CatchAccent = styled.div`
  width: 48px;
  height: 48px;
  background: #FF5A5A;
  border-radius: 12px;
    display: flex;
    align-items: center;
  justify-content: center;
  position: absolute;
  left: -64px;
  top: 0;
  @media (max-width: 800px) {
    position: static;
    margin-bottom: 16px;
    left: 0;
  }
`;

const CatchSimpleHeadline = styled.h2`
  color: #FF5A5A;
  font-size: 1.45rem;
  font-weight: 800;
  margin-bottom: 12px;
  margin-left: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CatchSimpleText = styled.p`
  color: rgba(255,255,255,0.92);
  font-size: 1.13rem;
  line-height: 1.7;
  margin-bottom: 0;
`;

const CatchSimpleCallout = styled.div`
  color: #fff;
  background: rgba(255,90,90,0.13);
  border-radius: 8px;
  padding: 12px 18px;
  font-weight: 600;
  margin-top: 18px;
  font-size: 1.08rem;
  letter-spacing: 0.01em;
  border-left: 4px solid #FF5A5A;
`;

const BusinessHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  padding: 15px 20px;
  z-index: 1000;
  border-bottom: 1px solid rgba(255, 90, 90, 0.2);
`;

const HeaderLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 500;
  transition: color 0.3s ease;

  &:hover {
    color: #FF5A5A;
  }
`;

const TableOfContents = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1.5px solid rgba(255,90,90,0.10);
  border-radius: 18px;
  padding: 32px;
  margin: 40px auto;
  max-width: 900px;
`;

const ContentsTitle = styled.h3`
  color: #FF5A5A;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 20px;
`;

const ContentsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContentsItem = styled.li`
  margin-bottom: 12px;
  
  a {
    color: rgba(255,255,255,0.85);
    text-decoration: none;
    font-size: 1.1rem;
    transition: color 0.3s ease;
    
    &:hover {
      color: #FF5A5A;
    }
  }
`;

const SectionAnchor = styled.div`
  position: relative;
  top: -100px;
  visibility: hidden;
`;

const BackToTopButton = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: #FF5A5A;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
`;

const BusinessPage = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const [showBackToTop, setShowBackToTop] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const benefits = [
    {
      icon: <FaCamera />,
      title: "Free Camera Installation",
      description: "Get a professional-grade camera installed at no cost. Our contractors ensure a clean, high-quality setup that matches your existing infrastructure."
    },
    {
      icon: <FaTools />,
      title: "Zero-Cost Setup",
      description: "We pay for all the parts and installation. A typical setup includes the camera and two ethernet cables—everything you need, covered by us."
    },
    {
      icon: <FaAd />,
      title: "Free Featured Advertising",
      description: "Turn your camera coverage into visibility for your business. Get free ads in our app, shown to thousands of nearby drivers — bringing more foot traffic from your local community."
    }
  ];

  const partners = [
    { name: "101 Music", url: "https://www.yelp.com/biz/101-music-san-francisco" },
    { name: "901 Columbus Cafe", url: "https://www.901columbuscafe.com/" },
    { name: "Acquolina", url: "https://www.yelp.com/biz/acquolina-san-francisco" },
    { name: "Biordi Art Imports", url: "https://biordi.com/" },
    { name: "Caffe Centro SP", url: "https://www.yelp.com/biz/caffe-centro-san-francisco" },
    { name: "Columbus Cyclery", url: "https://columbuscyclery.com/" },
    { name: "Dream Masters Tattoos", url: "https://www.yelp.com/biz/dream-masters-tattoos-san-francisco" },
    { name: "Felicity's Fetiche", url: "https://www.yelp.com/biz/felicitys-fetiche-san-francisco" },
    { name: "Golden Eagle Market", url: "https://www.yelp.com/biz/golden-eagle-market-san-francisco" },
    { name: "Infinite Martial Arts", url: "https://infinite-martialarts.com/" },
    { name: "Jamra Pizzeria", url: "https://jamrapizzeriagrill.com/" },
    { name: "Little Szechuan", url: "https://chowbusmkt.wixsite.com/littleszechuan" },
    { name: "Nick's Corner Store", url: "https://www.yelp.com/biz/nicks-corner-store-san-francisco" },
    { name: "Presidio Food Mart", url: "https://www.yelp.com/biz/presidio-food-mart-san-francisco" },
    { name: "Ralph Gracie BJJ", url: "https://www.ralphgracie.com/" },
    { name: "Tattoo Boogaloo", url: "https://www.tattooboogaloo.com/" },
    { name: "The Nature Shop", url: "https://www.yelp.com/biz/the-nature-stop-san-francisco" },

  ];

  const stats = [
    { number: "50+", label: "Cameras Installed in SF" },
    { number: "45+", label: "Hours Saved for Drivers" },
    { number: "100%", label: "Free for Partners" }
  ];

  return (
    <>
      <BusinessHeader>
        <HeaderLink to="/">
          ← Back to ParkingKarma main website
        </HeaderLink>
      </BusinessHeader>

      <Section>
        <Container>
          <HeroSection>
            <Overline>Partner With Us</Overline>
            <HeroTitle>
              Get free cameras, free ads, and more customers.
            </HeroTitle>
            <HeroSubtitle>
              Join San Francisco's fastest-growing network of businesses making parking easier — and your neighborhood safer.
            </HeroSubtitle>
          </HeroSection>

          <TableOfContents>
            <ContentsTitle>Quick Navigation FAQ</ContentsTitle>
            <ContentsList>
              <ContentsItem>
                <a href="#what-you-get">What You Get</a>
              </ContentsItem>
              <ContentsItem>
                <a href="#whats-the-catch">What's the Catch?</a>
              </ContentsItem>
              <ContentsItem>
                <a href="#how-we-make-money">How We Make Money</a>
              </ContentsItem>
              <ContentsItem>
                <a href="#privacy">Your Privacy is Protected</a>
              </ContentsItem>
              <ContentsItem>
                <a href="#installation">Simple Installation</a>
              </ContentsItem>
              <ContentsItem>
                <a href="#after-installation">What Happens After Installation?</a>
              </ContentsItem>
              <ContentsItem>
                <a href="#trusted-by">What other stores do we partner with?</a>
              </ContentsItem>
            </ContentsList>
          </TableOfContents>

          <StatsSection>
            {stats.map((stat, index) => (
              <StatCard key={index}>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsSection>

          <SectionAnchor id="what-you-get" />
          <SectionHeader>
            <Title>What You Get</Title>
            <Subtitle>Everything you need to help drivers find parking — at zero cost to you.</Subtitle>
          </SectionHeader>

          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitCard>
            ))}
          </BenefitsGrid>

          <SectionAnchor id="whats-the-catch" />
          <SectionHeader>
            <Title>What's the catch?</Title>
          </SectionHeader>
          <CatchSimpleSection
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <CatchSimpleHeadline>
              So it seems too good to be true, right?
            </CatchSimpleHeadline>
            <CatchSimpleText>
              We're not handing out free cameras just to be nice — this is a business too.
              <br /><br />
              Without cameras, we can't detect street parking in real-time.<br />
              Without real-time data, we don't have users.<br />
              And without users, we don't make money.
              <br /><br />
              The more cameras we install, the better the app works for everyone — and the more value we create for you and your neighborhood.
              <br /><br />
              Long-term, we think we can build something that rivals Google Maps.
            </CatchSimpleText>
          </CatchSimpleSection>

          <Divider />

          <SectionAnchor id="privacy" />
          <SectionHeader>
            <Title>Your Privacy is Protected</Title>
          </SectionHeader>

          <InfoRow
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <InfoIcon>
              <FaShieldAlt />
            </InfoIcon>
            <InfoContent>
              <InfoTitle>We Respect Your Network</InfoTitle>
              <InfoList>
                <li>We don't collect, store, or sell any data from your network.</li>
                <li>The only thing we connect to is a secure, encrypted video link to detect open parking spots.</li>
              </InfoList>
              <InfoBlockquote>
                It's like giving someone your phone number — they can call you, but they can't see inside your phone.<br />
                Same with your WiFi: we use the connection, but we can't see or access anything else.
              </InfoBlockquote>
            </InfoContent>
          </InfoRow>

          <Divider />

          <SectionAnchor id="installation" />
          <SectionHeader>
            <Title>Simple Installation</Title>
          </SectionHeader>

          <InfoRow
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <InfoIcon>
              <FaPlug />
            </InfoIcon>
            <InfoContent>
              <InfoTitle>One Wire. That's It.</InfoTitle>
              <InfoList>
                <li>A single wire runs from the camera to your modem.</li>
                <li>That same wire powers the camera — no extra cables.</li>
                <li>Our team handles everything — you don't lift a finger.</li>
              </InfoList>
              <InfoCallout>
                If drilling is needed, our contractors carefully drill and seal one clean hole for the wire.
                If it's not necessary, we don't drill — simple as that.
              </InfoCallout>
            </InfoContent>
          </InfoRow>

          <Divider />

          <SectionAnchor id="after-installation" />
          <SectionHeader>
            <Title>What Happens After Installation?</Title>
          </SectionHeader>

          <InfoRow
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <InfoIcon>
              <FaUsers />
            </InfoIcon>
            <InfoContent>
              <InfoTitle>We get out of your way — and you keep the perks.</InfoTitle>
              <ValueText>
                We know your priority is your business — not us. So we make things easy, stay out of the way, and always have your back.
              </ValueText>
              <ValueText>
                <br></br>
              </ValueText>
              <ValueText>
                If there's ever an issue or the camera needs maintenance, we show up — fast. No drama, no hassle. And if you ever need anything, we're just a call or text away.
              </ValueText>

              <InfoBlockquote>
                The ideal partnership is simple: we come in, install the camera, and that's it. You keep doing what you do best. You won't hear from us unless you want to.
              </InfoBlockquote>
            </InfoContent>
          </InfoRow>

          <Divider />

          <SectionAnchor id="how-we-make-money" />
          <SectionHeader>
            <Title>So how do we make money?</Title>
          </SectionHeader>

          <InfoRow
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <InfoIcon>
              <FaMoneyBillWave />
            </InfoIcon>
            <InfoContent>
              <ValueText>
                The app is free for everyone right now — and we want it to stay that way for everyday drivers.
                <br /><br />
                Our plan is to sell hyperlocal, targeted ads to businesses. Once we've got strong coverage across the city, regional and national brands — like Starbucks or Dunkin' — will want in.
                <br /><br />
                It's the same model Google Maps uses, just way more relevant at the street level.
              </ValueText>
              <InfoCallout>
                And when we do start running ads, don't worry: you'll always have your own promotion in the spaces your cameras cover. That's our commitment to you.
              </InfoCallout>
            </InfoContent>
          </InfoRow>

          <Divider />

          <ContactSection>
            <Title>Ready to Get Started?</Title>
            <ContactName>Drake Burciaga</ContactName>
            <ContactTitle>CEO, ParkingKarma</ContactTitle>
            <ContactPhone href="tel:214-422-9804">📱 214-422-9804</ContactPhone>
            <br></br>
            <ContactPhone href="mailto:drake@parkingkarma.com">📧 drake@parkingkarma.com</ContactPhone>
            <Subtitle>Call, email, or text anytime to schedule your free installation.</Subtitle>

          </ContactSection>

          <PartnersSection>
            <SectionAnchor id="trusted-by" />
            <Title>Trusted by San Francisco's Best</Title>
            <Subtitle>Join our growing network of partner businesses</Subtitle>
            <PartnerGrid>
              {partners.map((partner, index) => (
                <PartnerCard
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.04 }}
                >
                  <PartnerIcon>
                    <FaStore />
                  </PartnerIcon>
                  <PartnerName>{partner.name}</PartnerName>
                  <PartnerLink
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Site <FaExternalLinkAlt style={{ fontSize: "0.95em" }} />
                  </PartnerLink>
                </PartnerCard>
              ))}
            </PartnerGrid>
          </PartnersSection>

          <Divider />

        </Container>
      </Section>

      {showBackToTop && (
        <BackToTopButton
          onClick={scrollToTop}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ↑ Back to Top
        </BackToTopButton>
      )}
    </>
  );
};

export default BusinessPage;
